import {
  createModalStackActions,
  createModalStackReducer,
  DEFAULT_MODAL_STACK_KEY,
  EMPTY_MODAL_STACK,
  type ModalStackItem,
} from './modalStack';

export interface ModalStackStore {
  getStack: () => ModalStackItem[];
  subscribe: (listener: () => void) => () => void;
  push: (item: ModalStackItem) => void;
  remove: (id: string) => void;
  replace: (item: ModalStackItem) => void;
  clear: () => void;
}

export interface ModalReduxStore {
  dispatch: (action: { type: string }) => unknown;
  getState: () => unknown;
  subscribe: (listener: () => void) => () => void;
}

export interface ReduxModalStackStoreOptions {
  key?: string;
}

export const createReduxModalStackStore = (
  store: ModalReduxStore,
  { key = DEFAULT_MODAL_STACK_KEY }: ReduxModalStackStoreOptions = {}
): ModalStackStore => {
  const actions = createModalStackActions(key);

  const getStack = () =>
    (store.getState() as Record<string, ModalStackItem[] | undefined> | undefined)?.[key] ?? EMPTY_MODAL_STACK;

  return {
    getStack,
    subscribe: (listener) => store.subscribe(listener),
    push: (item) => {
      store.dispatch(actions.openModal(item));
    },
    remove: (id) => {
      store.dispatch(actions.closeModal(id));
    },
    replace: (item) => {
      store.dispatch(actions.replaceModal(item));
    },
    clear: () => {
      store.dispatch(actions.closeAllModals());
    },
  };
};

const createLocalModalStore = (): ModalReduxStore => {
  const reducer = createModalStackReducer();
  let state = { [DEFAULT_MODAL_STACK_KEY]: reducer(undefined, { type: '@@modalStack/init' }) };
  const listeners = new Set<() => void>();

  return {
    getState: () => state,
    dispatch: (action) => {
      const modalStack = reducer(state[DEFAULT_MODAL_STACK_KEY], action);

      if (modalStack === state[DEFAULT_MODAL_STACK_KEY]) return action;

      state = { [DEFAULT_MODAL_STACK_KEY]: modalStack };
      listeners.forEach((listener) => listener());

      return action;
    },
    subscribe: (listener) => {
      listeners.add(listener);

      return () => {
        listeners.delete(listener);
      };
    },
  };
};

export const createModalStackStore = (): ModalStackStore => createReduxModalStackStore(createLocalModalStore());
